/**
 * Detail-page building blocks.
 *
 * The old detail pages were a `<Card>` per topic with a three-column grid of
 * label/value rows inside, re-implemented per page — so "Email" was bold on one
 * page and muted on the next, and a null value rendered as "—", "None", or an
 * empty cell depending on who wrote it. These are that pattern, once.
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export function DetailPanel({
  title,
  icon: Icon,
  description,
  actions,
  children,
  className,
  body_class,
  test_id,
}: {
  title: string;
  icon?: LucideIcon;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  body_class?: string;
  test_id?: string;
}) {
  return (
    <section className={cn('console-panel flex flex-col', className)} data-testid={test_id}>
      <header className="flex items-start justify-between gap-3 border-b border-border px-3.5 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <div className="min-w-0">
            <h2 className="truncate font-heading text-sm font-bold">{title}</h2>
            {description && (
              <p className="truncate text-micro text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </header>
      <div className={cn('min-w-0 flex-1 p-3.5', body_class)}>{children}</div>
    </section>
  );
}

/**
 * A label/value list.
 *
 * A real `<dl>`, so a screen reader reads "Email, none" as a pair rather than
 * as two unrelated strings. Two columns on anything wider than a phone; the
 * label column is fixed so values line up down the panel.
 */
export function FieldList({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <dl className={cn('flex flex-col divide-y divide-border/60', className)}>{children}</dl>;
}

export function Field({
  label,
  value,
  /** Rendered instead of the placeholder when `value` is null/undefined/''. */
  empty = '—',
  mono,
  className,
  test_id,
}: {
  label: string;
  value: React.ReactNode;
  empty?: string;
  /** Tabular figures — for ids, amounts, phone numbers. */
  mono?: boolean;
  className?: string;
  test_id?: string;
}) {
  const is_empty = value === null || value === undefined || value === '';
  return (
    <div
      className={cn('grid grid-cols-[minmax(6rem,9rem)_1fr] gap-3 py-2 first:pt-0 last:pb-0', className)}
      data-testid={test_id}
    >
      <dt className="text-data font-medium text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'min-w-0 break-words text-data',
          mono && 'tabular',
          is_empty && 'text-muted-foreground/60',
        )}
      >
        {is_empty ? empty : value}
      </dd>
    </div>
  );
}

/** A small "nothing here" line for an empty panel body. */
export function PanelEmpty({
  icon: Icon,
  children,
}: {
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-6 text-center">
      {Icon && (
        <span className="flex h-9 w-9 items-center justify-center rounded-panel bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <p className="text-data text-muted-foreground">{children}</p>
    </div>
  );
}

/**
 * Skeleton for a panel that hasn't loaded.
 *
 * Sized to the panel it replaces so the page doesn't reflow when data lands —
 * the previous pages showed a single centred spinner and then jumped.
 */
export function PanelSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2.5">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-[minmax(6rem,9rem)_1fr] gap-3">
          <Bar width="60%" />
          <Bar width={`${[80, 55, 70, 45][i % 4]}%`} />
        </div>
      ))}
    </div>
  );
}

function Bar({ width }: { width: string }) {
  return (
    <span className="relative block h-3.5 overflow-hidden rounded-pill bg-muted" style={{ width }}>
      <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent" />
    </span>
  );
}
